"use client"; // Marking the file as a client component

import Confetti from "react-confetti";
import Link from 'next/link'



import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaClock,
  FaMapMarkerAlt,
  FaTrophy,
  FaMoon,
  FaMoneyBillWave,
} from "react-icons/fa";

const benefits = [
  {
    id: 1,
     slug: 'stuck-with-only-local-customers',
    title: "Stuck with Only Local Customers?",
    subtitle: "Go Digital, Grow Nationwide",
    icon: FaMapMarkerAlt,
    color: "bg-gradient-to-r from-red-500 to-orange-600", // Urgent growth vibe
    description:
      "🌍 Limited to walk-ins and local ads? \n✅ Solution: Reach customers across your state with 24/7 online presence. Show up in Google searches and social media!",
    stat: "78% local searches lead to online visits",
    cta: "See Nearby Success Stories →",
    image: "/image/blog/local-to-global-growth.jpg"
  },
  {
    id: 2,
     slug: 'losing-to-tech-savvy-competitors',
    title: "Losing to Tech-Savvy Competitors?",
    subtitle: "Look More Professional Instantly",
    icon: FaTrophy,
    color: "bg-gradient-to-r from-blue-500 to-teal-600", // Trust/professional combo
    description:
      "📉 Customers choosing 'modern' businesses? \n✅ Solution: Get a polished website that makes you look established. Showcase your best work/services!",
    stat: "94% judge businesses without websites as unreliable",
    cta: "View Before/After Comparisons ⇄",
    image: "/image/blog/professional-website-transformation.jpg"
  },
  {
    id: 3,
    slug: 'wasting-time-on-customer-calls',
    title: "Wasting Time on Customer Calls?",
    subtitle: "Automate FAQs & Bookings",
    icon: FaClock,
    color: "bg-gradient-to-r from-green-500 to-lime-600", // Fresh time-saving energy
    description:
      "⏰ Explaining prices/services repeatedly? \n✅ Solution: Let your website answer questions and take bookings automatically!",
    stat: "Saves 15+ hours/month on calls",
    cta: "Try Our Booking Demo ⏲️",
    image: "/image/blog/time-saving-automation.jpg"
  },
  {
    id: 4,
     slug: 'wasting-time-on-customer-calls',
    title: "Missing Night/Weekend Sales?",
    subtitle: "24/7 Sales Assistant",
    icon: FaMoon,
    color: "bg-gradient-to-r from-purple-600 to-indigo-700", // Night/mystery colors
    description:
      "🌙 Closing hours = lost opportunities? \n✅ Solution: Your website works while you sleep! Take orders and showcase services anytime.",
    stat: "35% online orders happen after 9PM",
    cta: "See Night Traffic Reports 🌃",
    image: "/image/blog/24-7-nighttime-sales.jpg"
  },
  {
    id: 5,
    slug: 'scared-of-tech-costs',
    title: "Scared of Tech Costs?",
    subtitle: "Pay Once, Benefit Forever",
    icon: FaMoneyBillWave,
    color: "bg-gradient-to-r from-yellow-400 to-amber-600", // Energetic money colors
    description:
      "💸 Thinking websites are expensive? \n✅ Solution: Affordable packages starting at $199. No monthly fees!",
    stat: "60% cheaper than DIY platforms",
    cta: "Compare Pricing Plans 💰",
    image: "/image/blog/affordable-website-value.jpg"
  }
];




const BenefitsSection = () => {
  const [selectedBenefit, setSelectedBenefit] = useState(null);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  const handleBenefitClick = (benefit) => {
    setSelectedBenefit(benefit);
  };

  const closeModal = () => {
    setSelectedBenefit(null);
  };

  return (
    <section className="relative px-4 py-8 pt-32 dark:bg-gray-900 z-0" id="benefits-section">
      
      <div className="max-w-screen-2xl mx-auto">
        {/* Top Gradient Background */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-40 z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
          data-aos="fade-up"
        >
          <div
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#42cf70] to-[#0077ff] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
          />
        </div>
        <h1
  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-center text-light-text-primary dark:text-dark-text-primary mb-12 sm:mb-16 md:mb-20 leading-tight"
  data-aos="fade-up"
>
  <span className="relative inline-block">
    Why Your Business
    <span
      className="absolute -bottom-1.5 sm:-bottom-2 left-0 w-full h-1 sm:h-1.5 bg-primary-500 opacity-50"
      aria-hidden="true"
    />
  </span>
  <br />
  <span className="text-primary-500 hover:text-primary-600 transition-colors duration-300 cursor-pointer">
    Needs a Website?
  </span>
</h1>

        <div className="relative wrap overflow-hidden h-full z-20">
          <div className="border-2-2 absolute border-opacity-20 border-gray-700 h-full border left-1/2"></div>
          {benefits.map((benefit, index) => (
            <div
              key={benefit.id}
              className={`mb-8 flex flex-col md:flex-row justify-between items-center w-full ${
                index % 2 === 0 ? "md:flex-row-reverse" : "md:flex-row"
              }`}
              data-aos="fade-up"
            >
              <div className="order-1 w-full md:w-5/12"></div>
              <div className="z-20 flex items-center order-1 bg-gray-800 shadow-xl w-8 h-8 rounded-full min-w-fit p-2">
                <h1 className="mx-auto font-semibold text-lg text-white">
                  {benefit.date}
                </h1>
              </div>
              <button
                onClick={() => handleBenefitClick(benefit)}
                className={`relative order-1 w-full md:w-5/12 px-6 py-4 rounded-lg shadow-xl ${benefit.color} text-white cursor-pointer transition-all duration-300 transform  focus:outline-none`}
              >
                <img
                  src={benefit.image}
                  alt={benefit.title}
                  className="w-full h-auto rounded-lg mb-4"
                />
                <h3 className="mb-3 font-bold text-xl">{benefit.title}</h3>
                <benefit.icon className="text-4xl mb-3 inline-block" />
                <p className="text-sm leading-snug tracking-wide">
                  Click for more details
                </p>
              </button>
            </div>
          ))}
        </div>

        {/* Bottom Gradient Background */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-[calc(100%-13rem)] z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
        >
          <div
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
            className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#42cf70] to-[#0077ff] opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
          />
        </div>

        {selectedBenefit && (
      <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-hidden h-full w-full z-50 flex items-center justify-center p-4"
      onClick={closeModal}
    >
      {/* Confetti Animation - Only for the first item */}
      {selectedBenefit.id === 1 && ( // Replace `1` with the ID or condition for the first item
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false} // Stop confetti after a few seconds
          numberOfPieces={300} // Adjust the amount of confetti
          gravity={0.2} // How fast confetti falls
          initialVelocityY={20} // Speed of confetti
          colors={["#42cf70", "#0077ff", "#ff6b6b", "#f9c74f"]} // Custom colors
        />
      )}
    
      {/* Modal Content */}
      <div
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 ease-out scale-95 hover:scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 text-center">
        {/* Icon with subtle animation */}
<div className="animate-bounce">
  <selectedBenefit.icon
    className="mx-auto text-6xl text-gray-800 dark:text-white"
  />
</div>

    
          {/* Title */}
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-6">
            {selectedBenefit.title}
          </h3>
    
          {/* Description */}
          <p className="mt-4 text-gray-600 dark:text-gray-300 leading-relaxed">
            {selectedBenefit.description}
          </p>

          {/* “Learn More” Link */}
<div className="mt-6">
  <Link
    href={`/blog/${selectedBenefit.slug}`}
    className="inline-block text-primary font-medium hover:underline"
  >
    Learn more about “{selectedBenefit.title}” →
  </Link>
</div>
    
          {/* Close Button */}
          <div className="mt-8">
            <button
              onClick={closeModal}
              className="px-6 py-3 bg-primary text-white font-semibold rounded-full hover:opacity-90 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#42cf70]"
            >
              Got it, thanks!
            </button>
          </div>
        </div>
    
        {/* Close Icon (Optional) */}
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
        )}
      </div>
    </section>
  );
};

export default BenefitsSection;